import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Plus, Trash2, GripVertical } from 'lucide-react';
import { useInspectionTemplate, useInspectionTemplateSections, useInspectionTemplateItems } from '@/hooks/useInspections';
import { InspectionTemplateItem } from '@/types/inspection';
import { toast } from 'sonner';

interface TemplateEditorProps {
  templateId: string;
  onBack: () => void;
}

type ResponseType = 'PASS_FAIL_NA' | 'YES_NO' | 'TEXT' | 'NUMBER' | 'DROPDOWN';

export const TemplateEditor: React.FC<TemplateEditorProps> = ({ templateId, onBack }) => {
  const { data: template, isLoading: templateLoading } = useInspectionTemplate(templateId);
  const { sections, isLoading: sectionsLoading, createSection, updateSection, deleteSection } = useInspectionTemplateSections(templateId);
  const { items, isLoading: itemsLoading, createItem, updateItem, deleteItem } = useInspectionTemplateItems(templateId);

  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionSubsection, setNewSectionSubsection] = useState('');

  const handleAddSection = async () => {
    if (!newSectionName.trim()) {
      toast.error('Section name is required');
      return;
    }
    
    await createSection({
      template_id: templateId,
      name: newSectionName,
      subsection: newSectionSubsection || null,
      sort_order: (sections?.length || 0) + 1
    });
    
    setNewSectionName('');
    setNewSectionSubsection('');
    toast.success('Section added');
  };

  const handleAddItem = async (sectionId: string) => {
    const sectionItems = items?.filter(i => i.section_id === sectionId) || [];
    
    await createItem({
      section_id: sectionId,
      item_text: 'New checklist item',
      response_type: 'PASS_FAIL_NA',
      sort_order: sectionItems.length + 1,
      notes_required_if_fail: false,
      photo_required_if_fail: false,
      always_require_notes: false
    });
    
    toast.success('Item added');
  };

  const handleUpdateItem = async (itemId: string, updates: Partial<InspectionTemplateItem>) => {
    await updateItem({ id: itemId, ...updates });
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteItem(itemId);
    toast.success('Item deleted');
  };

  const handleDeleteSection = async (sectionId: string) => {
    const sectionItems = items?.filter(i => i.section_id === sectionId) || [];
    if (sectionItems.length > 0) {
      toast.error('Delete all items in this section first');
      return;
    }
    await deleteSection(sectionId);
    toast.success('Section deleted');
  };

  const getSectionItems = (sectionId: string) => {
    return items?.filter(i => i.section_id === sectionId).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];
  };

  if (templateLoading || sectionsLoading || itemsLoading || !template) {
    return <div className="flex items-center justify-center p-8">Loading template...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>
        <div>
          <h2 className="text-xl font-semibold">{template.name}</h2>
          <p className="text-sm text-muted-foreground">{template.description}</p>
        </div>
      </div>

      {/* Add Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add New Section</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Section Name</Label>
              <Input
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="e.g., Fire Safety"
              />
            </div>
            <div className="flex-1">
              <Label>Subsection (optional)</Label>
              <Input
                value={newSectionSubsection}
                onChange={(e) => setNewSectionSubsection(e.target.value)}
                placeholder="e.g., Extinguishers"
              />
            </div>
            <Button onClick={handleAddSection}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Accordion type="multiple" className="space-y-4">
        {sections?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((section) => (
          <AccordionItem key={section.id} value={section.id} className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{section.name}</span>
                {section.subsection && (
                  <span className="text-sm text-muted-foreground">/ {section.subsection}</span>
                )}
                <span className="text-xs bg-muted px-2 py-1 rounded">
                  {getSectionItems(section.id).length} items
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pt-4">
              {/* Section Items */}
              {getSectionItems(section.id).map((item) => (
                <Card key={item.id} className="bg-muted/30">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Label>Item Text</Label>
                        <Input
                          value={item.item_text}
                          onChange={(e) => handleUpdateItem(item.id, { item_text: e.target.value })}
                        />
                      </div>
                      <div className="w-40">
                        <Label>Response Type</Label>
                        <Select
                          value={item.response_type}
                          onValueChange={(value: ResponseType) => handleUpdateItem(item.id, { response_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PASS_FAIL_NA">Pass/Fail/NA</SelectItem>
                            <SelectItem value="YES_NO">Yes/No</SelectItem>
                            <SelectItem value="TEXT">Text</SelectItem>
                            <SelectItem value="NUMBER">Number</SelectItem>
                            <SelectItem value="DROPDOWN">Dropdown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive mt-6"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex gap-6">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={item.notes_required_if_fail || false}
                          onCheckedChange={(checked) => handleUpdateItem(item.id, { notes_required_if_fail: checked })}
                        />
                        <Label className="text-sm">Notes required if Fail</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={item.photo_required_if_fail || false}
                          onCheckedChange={(checked) => handleUpdateItem(item.id, { photo_required_if_fail: checked })}
                        />
                        <Label className="text-sm">Photo required if Fail</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={item.always_require_notes || false}
                          onCheckedChange={(checked) => handleUpdateItem(item.id, { always_require_notes: checked })}
                        />
                        <Label className="text-sm">Always require notes</Label>
                      </div>
                    </div>

                    {item.response_type === 'DROPDOWN' && (
                      <div>
                        <Label>Dropdown Options (comma-separated)</Label>
                        <Input
                          value={Array.isArray(item.dropdown_options) ? item.dropdown_options.join(', ') : ''}
                          onChange={(e) => handleUpdateItem(item.id, { 
                            dropdown_options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          })}
                          placeholder="Option 1, Option 2, Option 3"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleAddItem(section.id)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-destructive"
                  onClick={() => handleDeleteSection(section.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Section
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {(!sections || sections.length === 0) && (
        <div className="text-center py-8 text-muted-foreground">
          No sections yet. Add your first section above.
        </div>
      )}
    </div>
  );
};
